import Image from 'next/image';
import { FC } from 'react';

import { SECTIONS_IDS } from '../../../constants/sections';
import { useLkBasePath } from '../../../hooks/use-lk-base-path';
import { ButtonLink } from '../common/ButtonLink';
import { Container } from '../common/Container';
import { Flex } from '../common/Flex';
import { Link } from '../common/Link';
import { Text } from '../common/Text';
import {
  WhySgSmartRoutingImpactTrendIcon,
  WhySgSmartRoutingValueMoneyIcon,
} from '../whySg/icons/WhySgSmartRoutingIcons';

import styles from './WhySgSmartRoutingBlock.module.scss';

const ROUTING_DIAGRAM_SRC = '/assets/why_sg_routing_diagram.png';

export const WhySgSmartRoutingBlock: FC = () => {
  const lkBasePath = useLkBasePath();

  return (
    <Container className={styles.root}>
      <div className={styles.layout}>
        <Flex direction={'column'} gap={4} className={styles.intro}>
          <div className={styles.sectionLabel}>
            <span className={styles.sectionLabelLine} aria-hidden />

            <Text type={'medium14'} color={'green'} className={styles.sectionEyebrow}>
              Smart Routing
            </Text>
          </div>

          <Text type={'regular48'} color={'white'} className={styles.title}>
            Maximize Revenue with Smart Routing
          </Text>

          <Text type={'regular16'} color={'white50'} whiteSpace={'normal'} className={styles.description}>
            Our intelligent routing engine analyzes hundreds of variables in real-time to determine the optimal path for
            every transaction — maximizing approval rates and minimizing costs.
          </Text>
        </Flex>

        <div className={styles.cardsDiagramRow}>
          <div className={styles.cardsColumn}>
            <article className={styles.card}>
              <Flex className={styles.cardInner} direction={'column'} alignItems={'flexStart'} gap={4}>
                <Flex direction={'row'} alignItems={'center'} gap={4}>
                  <Flex className={styles.iconWrap} alignItems={'center'} justifyContent={'center'} aria-hidden>
                    <WhySgSmartRoutingImpactTrendIcon />
                  </Flex>

                  <Text type={'medium16'} color={'white'}>
                    Impact
                  </Text>
                </Flex>

                <Text type={'medium18'} color={'white'}>
                  Boost your Approval Ratio by up to <span className={styles.highlight}>20%</span>
                </Text>

                <Text type={'regular14'} color={'white50'} whiteSpace={'normal'}>
                  Real-time analysis of transaction data, card type, geography and acquirer performance to route each
                  payment optimally.
                </Text>
              </Flex>
            </article>

            <article className={styles.card}>
              <Flex className={styles.cardInner} direction={'column'} alignItems={'flexStart'} gap={4}>
                <Flex direction={'row'} alignItems={'center'} gap={4}>
                  <Flex className={styles.iconWrap} alignItems={'center'} justifyContent={'center'} aria-hidden>
                    <WhySgSmartRoutingValueMoneyIcon />
                  </Flex>

                  <Text type={'medium16'} color={'white'}>
                    Value
                  </Text>
                </Flex>

                <Text type={'medium18'} color={'white'} whiteSpace={'normal'}>
                  Reduce failed transactions and capture more revenue automatically
                </Text>

                <Text type={'regular14'} color={'white50'} whiteSpace={'normal'}>
                  Automatic failover and retry logic ensures no revenue is left on the table. Smart cascading across
                  multiple acquirers.
                </Text>
              </Flex>
            </article>

            <Flex className={styles.actions} wrap={'wrap'} alignItems={'center'}>
              <ButtonLink className={styles.ctaFilled} href={`${lkBasePath}/registration`} isOutsideLink>
                Become a merchant
              </ButtonLink>

              <Link href={`#${SECTIONS_IDS.contact}`} className={styles.docsLink}>
                Request demo
              </Link>
            </Flex>
          </div>

          <div className={styles.diagramWrap}>
            <Image
              className={styles.diagram}
              src={ROUTING_DIAGRAM_SRC}
              alt="Diagram: routing engine linking payment methods to acquirers with approval rates"
              width={1012}
              height={1042}
              layout="responsive"
              sizes="(min-width: 960px) 50vw, 100vw"
            />
          </div>
        </div>
      </div>
    </Container>
  );
};
